import { actionDraw } from "../deck/actionDraw.js";
import { lookupCard } from "../../content/cards.js";
import type { ActionEffect } from "../types/index.js";
import { applyDrawPerAction, applyDrawPerWorker } from "./drawPer.js";
import type { ApplyEffectContext } from "./effectContext.js";
import { startInn } from "./startInn.js";
import { startRepeatPlay } from "./startRepeatPlay.js";
import { startStables } from "./startStables.js";
import { startWorkerCycle } from "./startWorkerCycle.js";
import { startPsychologicalCounseling, startSpecialTaskTeam } from "./startIdleChoice.js";
import { createCardInstance, createCardInstances } from "../deck/createInstance.js";
import {
  startCellarChoice,
  startFieldJudgmentChoice,
  startFocusChoice,
  startKnowledgeShareChoice,
  startQualityChoice,
  startSendoffChoice,
  startTrainingChoice,
} from "../choices/startChoice.js";

export type { ApplyEffectContext } from "./effectContext.js";
export type ApplyEffectResult = ApplyEffectContext;

function drawToHand(ctx: ApplyEffectContext, count: number): ApplyEffectContext {
  const result = actionDraw(ctx.run, count);
  return {
    ...ctx,
    reshuffled: ctx.reshuffled || result.reshuffled,
    run: {
      ...result.run,
      deck: result.deck,
      discard: result.discard,
      rngState: result.rngState,
      hand: [...ctx.run.hand, ...result.drawn],
    },
  };
}

function applyFatigue(ctx: ApplyEffectContext, amount: number): ApplyEffectContext {
  return { ...ctx, run: { ...ctx.run, fatigue: ctx.run.fatigue + amount } };
}

function drawWhileIntern(ctx: ApplyEffectContext, actions: number): ApplyEffectContext {
  let next = ctx;
  while (true) {
    const drawn = actionDraw(next.run, 1);
    const card = drawn.drawn[0];
    next = {
      ...next,
      reshuffled: next.reshuffled || drawn.reshuffled,
      run: { ...drawn.run, hand: card ? [...next.run.hand, card] : next.run.hand },
    };
    if (!card || card.cardId !== "intern") break;
  }
  return { ...next, run: { ...next.run, energy: next.run.energy + actions } };
}

function applySingle(ctx: ApplyEffectContext, effect: ActionEffect): ApplyEffectContext {
  switch (effect.kind) {
    case "caseExclude":
      return ctx.run.hand.length === 0 ? ctx : {
        ...ctx, run: { ...ctx.run, pendingChoice: { kind: "caseExclude", sourceInstanceId: ctx.sourceInstanceId, selected: [] } },
      };
    case "resolveObstruction":
      return { ...ctx, run: { ...ctx.run, inPlay: ctx.run.inPlay.filter((card) => card.instanceId !== ctx.sourceInstanceId) } };
    case "block":
      return { ...ctx, run: { ...ctx.run, block: ctx.run.block + effect.amount } };
    case "draw":
      return drawToHand(ctx, effect.count);
    case "drawToHandSize": {
      const next = drawToHand(ctx, Math.max(0, effect.size - ctx.run.hand.length));
      return { ...next, run: { ...next.run, energy: next.run.energy + effect.actions } };
    }
    case "drawAction": {
      const next = effect.draw > 0 ? drawToHand(ctx, effect.draw) : ctx;
      return { ...next, run: { ...next.run, energy: next.run.energy + effect.actions } };
    }
    case "progress":
      return {
        ...ctx,
        activeCase: {
          ...ctx.activeCase,
          pendingProgress: (ctx.activeCase.pendingProgress ?? 0) + effect.amount,
        },
      };
    case "investment":
      {
        const investment = lookupCard(effect.cardId)?.investment;
        const activeCase = {
          ...ctx.activeCase,
          investments: [...(ctx.activeCase.investments ?? []), effect.cardId],
          ...(investment === "progress" ? {
            pendingProgress: (ctx.activeCase.pendingProgress ?? 0) + 1,
          } : {}),
        };
        const next = { ...ctx, activeCase };
        if (investment === "draw") return drawToHand(next, 1);
        if (investment === "block") return { ...next, run: { ...next.run, block: next.run.block + 1 } };
        return next;
      }
    case "deadlineNegotiation":
      return {
        ...ctx,
        activeCase: {
          ...ctx.activeCase,
          deadline: ctx.activeCase.deadline + 1,
          workerProgressDisabled: true,
        },
      };
    case "cellar":
      return startCellarChoice(ctx, effect);
    case "knowledgeShare":
      return startKnowledgeShareChoice(ctx);
    case "fieldJudgment":
      return startFieldJudgmentChoice(ctx);
    case "meeting":
      return startRepeatPlay(ctx, effect.times ?? 2);
    case "drawPerAction":
      return applyDrawPerAction(ctx);
    case "workerCycle":
      return startWorkerCycle(ctx, effect.draw, effect.actions);
    case "stables":
      return startStables(ctx);
    case "psychologicalCounseling":
      return startPsychologicalCounseling(ctx);
    case "specialTaskTeam":
      return startSpecialTaskTeam(ctx);
    case "inn":
      return startInn(ctx);
    case "heal":
      return { ...ctx, run: { ...ctx.run, fatigue: Math.max(0, ctx.run.fatigue - effect.amount) } };
    case "fatigue":
      return applyFatigue(ctx, effect.amount);
    case "drawPerWorker":
      return applyDrawPerWorker(ctx);
    case "sendoff":
      return startSendoffChoice(ctx);
    case "training":
      return startTrainingChoice(ctx);
    case "gainWorkersToHand": {
      const created = createCardInstances(
        { nextCardSequence: ctx.run.nextCardSequence },
        Array.from({ length: effect.count }, () => effect.cardId),
      );
      return {
        ...ctx,
        run: {
          ...ctx.run,
          hand: [...ctx.run.hand, ...created.instances],
          nextCardSequence: created.nextCardSequence,
        },
      };
    }
    case "placementPlan": {
      const next = drawToHand(ctx, 1);
      const eligible = next.run.hand.length > 0
        && next.run.hand.every((card) => card.cardId === "intern" || card.cardId === "idle");
      return {
        ...next,
        activeCase: eligible
          ? { ...next.activeCase, pendingProgress: (next.activeCase.pendingProgress ?? 0) + 1 }
          : next.activeCase,
        run: { ...next.run, energy: next.run.energy + 1, fatigue: eligible ? Math.max(0, next.run.fatigue - 1) : next.run.fatigue },
      };
    }
    case "spotAssignment": {
      const next = drawToHand(ctx, 1);
      const hasIdle = next.run.hand.some((card) => card.cardId === "idle");
      return {
        ...next,
        activeCase: hasIdle
          ? { ...next.activeCase, pendingProgress: (next.activeCase.pendingProgress ?? 0) + 2 }
          : next.activeCase,
        run: { ...next.run, energy: next.run.energy + 1 },
      };
    }
    case "onboardInterns":
      return drawWhileIntern(ctx, effect.actions);
    case "workStandardization": {
      const internCount = ctx.run.hand.filter((card) => card.cardId === "intern").length;
      const onlyInterns = internCount > 0 && ctx.run.hand.every((card) => card.cardId === "intern");
      return {
        ...ctx,
        activeCase: {
          ...ctx.activeCase,
          pendingProgress: (ctx.activeCase.pendingProgress ?? 0) + internCount + (onlyInterns ? 3 : 0),
        },
      };
    }
    case "gainWorker": {
      const created = createCardInstance({ nextCardSequence: ctx.run.nextCardSequence }, effect.cardId);
      return {
        ...ctx,
        run: {
          ...ctx.run,
          discard: [...ctx.run.discard, created.instance],
          nextCardSequence: created.nextCardSequence,
        },
      };
    }
    case "focus":
      return startFocusChoice(ctx);
    case "quality":
      return startQualityChoice(ctx);
    case "compound": {
      let next = ctx;
      for (const sub of effect.effects) {
        next = applySingle(next, sub);
        if (next.run.pendingChoice) break;
      }
      return next;
    }
  }
}

export function applyEffect(ctx: ApplyEffectContext, effect: ActionEffect): ApplyEffectResult {
  return applySingle(ctx, effect);
}

export function applyCardEffects(
  ctx: ApplyEffectContext,
  effects: ActionEffect | undefined,
): ApplyEffectResult {
  const prepared = { ...ctx, run: { ...ctx.run, actionDrawRemaining: ctx.activeCase.actionDrawLimit } };
  return effects ? applySingle(prepared, effects) : prepared;
}
